/**
 * Data Fetcher Module - Paginated Storefront API Metaobject Loading
 *
 * PURPOSE: Replaces Liquid metaobject loops with client-side Storefront API fetches.
 * This eliminates Liquid complexity score from 50,000+ down to ~500 and prevents
 * production 500 errors while supporting unlimited dataset sizes via pagination.
 *
 * ARCHITECTURAL ROLE: Phase 1 Bootstrap Support Module
 * - Fetches ALL 4 metaobject types in parallel via Storefront API
 * - Uses cursor-based pagination (250 items/page) for unlimited dataset size
 * - Built-in throttling (500ms between pages) to stay under Shopify API rate limits
 * - Provides progress tracking for UX updates
 * - Graceful error handling with retry logic
 * - Caching to prevent duplicate fetches
 *
 * CCP COMPLIANCE:
 * - Phase 1 Bootstrap module (CCP Exempted - creates data for PPD)
 * - Does NOT access PPD or conductor (runs BEFORE they exist)
 * - No CoreEventBus dependency (bootstrap modules use DOM CustomEvents)
 * - Registered in PHASE_1_MODULES in md-config-ccp-validator.py
 *
 * DATA FLOW:
 * Liquid injects storefront token -> DataFetcher fetches metaobjects ->
 * Returns to initializer -> Merged into extractedData -> PPD creation via data-pipeline
 *
 * Dependencies:
 * - Storefront API access token (injected via Liquid)
 * - Network access to Shopify Storefront API
 */

// Dev-only logging (production guard)
const isDev = () =>
  window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

class DataFetcher {
  /**
   * @param {string} storefrontToken - Shopify Storefront API access token
   * @param {string} shopDomain - Shop domain (e.g., 'murphydoor.myshopify.com')
   */
  constructor(storefrontToken, shopDomain) {
    this.storefrontToken = storefrontToken;
    this.shopDomain = shopDomain;
    this.apiVersion = '2025-01';
    this.cache = new Map();

    // Throttling configuration
    this.throttleDelayMs = 360; // Safe rate (~166 req/min) with safety margin under Shopify's ~167 req/min limit, providing 28% improvement over original 500ms
    this.maxRetries = 3;
    this.retryDelayMs = 1000;
    this.timeoutMs = 10000; // 10 second timeout for API requests

    // Progress tracking
    this.progress = {
      total: 0,
      loaded: 0,
      types: {},
    };

    isDev() &&
      console.log('[DataFetcher] Initialized', {
        shopDomain: this.shopDomain,
        apiVersion: this.apiVersion,
        tokenPresent: !!this.storefrontToken,
      });
  }

  /**
   * Fetch a single page of metaobjects via Storefront API GraphQL
   *
   * @param {string} type - Metaobject type (e.g., 'wood', 'finishes')
   * @param {string|null} cursor - Pagination cursor (null for first page)
   * @param {number} batchSize - Number of items per page (max 250)
   * @returns {Promise<{nodes: Array, pageInfo: {hasNextPage: boolean, endCursor: string}}>}
   */
  async fetchPage(type, cursor = null, batchSize = 250) {
    if (!this.storefrontToken) {
      throw new Error('[DataFetcher] No Storefront Access Token configured');
    }

    const query = `query FetchMetaobjects($type: String!, $first: Int!${cursor ? ', $after: String' : ''}) {
      metaobjects(type: $type, first: $first${cursor ? ', after: $after' : ''}) {
        nodes {
          id
          handle
          type
          fields {
            key
            value
            type
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }`;

    const variables = {
      type,
      first: batchSize,
    };

    if (cursor) {
      variables.after = cursor;
    }

    const url = `https://${this.shopDomain}/api/${this.apiVersion}/graphql.json`;

    // Create an AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({ query, variables }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(
          `[DataFetcher] HTTP ${response.status}: ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.errors) {
        isDev() &&
          console.error('[DataFetcher] GraphQL errors:', result.errors);
        throw new Error(
          `[DataFetcher] GraphQL errors: ${JSON.stringify(result.errors)}`
        );
      }

      const metaobjects = result.data?.metaobjects;
      if (!metaobjects) {
        throw new Error(
          `[DataFetcher] No metaobjects data in response for type: ${type}`
        );
      }

      return {
        nodes: metaobjects.nodes || [],
        pageInfo: metaobjects.pageInfo || {
          hasNextPage: false,
          endCursor: null,
        },
      };
    } catch (error) {
      // Handle timeout/abort errors specifically
      if (error.name === 'AbortError') {
        throw new Error(
          `[DataFetcher] Request timeout after ${this.timeoutMs}ms for type: ${type}`
        );
      }
      throw error;
    } finally {
      clearTimeout(timeoutId); // Always clear timeout
    }
  }

  /**
   * Fetch ALL items of a specific metaobject type using cursor-based pagination.
   * Handles datasets of any size by following pagination cursors until exhausted.
   *
   * @param {string} type - Metaobject type to fetch
   * @param {number} batchSize - Items per page (default 250, max allowed by Shopify)
   * @returns {Promise<Array>} Complete array of ALL metaobject nodes for this type
   */
  async fetchAllOfType(type, batchSize = 250) {
    // Check cache first
    const cacheKey = `type:${type}`;
    if (this.cache.has(cacheKey)) {
      isDev() && console.log(`[DataFetcher] Cache hit for ${type}`);
      return this.cache.get(cacheKey);
    }

    isDev() &&
      console.log(
        `[DataFetcher] Fetching all ${type} metaobjects (batchSize: ${batchSize})...`
      );
    const startTime = performance.now();

    const allNodes = [];
    let cursor = null;
    let pageCount = 0;
    let hasNextPage = true;

    while (hasNextPage) {
      pageCount++;

      // Throttle between pages (not before the first request)
      if (pageCount > 1) {
        isDev() &&
          console.log(
            `[DataFetcher] Throttling ${this.throttleDelayMs}ms before page ${pageCount}...`
          );
        await this.sleep(this.throttleDelayMs);
      }

      let retries = 0;
      let pageResult = null;

      // Retry logic for transient failures
      while (retries < this.maxRetries) {
        try {
          pageResult = await this.fetchPage(type, cursor, batchSize);
          break; // Success, exit retry loop
        } catch (error) {
          retries++;
          if (retries >= this.maxRetries) {
            isDev() &&
              console.error(
                `[DataFetcher] Failed to fetch ${type} page ${pageCount} after ${this.maxRetries} retries:`,
                error
              );
            throw error;
          }
          isDev() &&
            console.warn(
              `[DataFetcher] Retry ${retries}/${this.maxRetries} for ${type} page ${pageCount}:`,
              error.message
            );
          await this.sleep(this.retryDelayMs * retries); // Exponential backoff
        }
      }

      allNodes.push(...pageResult.nodes);
      hasNextPage = pageResult.pageInfo.hasNextPage;
      cursor = pageResult.pageInfo.endCursor;

      // Update progress
      this.progress.types[type] = {
        pages: pageCount,
        items: allNodes.length,
        complete: !hasNextPage,
      };
      this.progress.loaded = Object.values(this.progress.types).reduce(
        (sum, t) => sum + t.items,
        0
      );

      isDev() &&
        console.log(
          `[DataFetcher] ${type} page ${pageCount}: ${pageResult.nodes.length} items (total: ${allNodes.length})`
        );
    }

    const duration = performance.now() - startTime;
    isDev() &&
      console.log(
        `[DataFetcher] ${type}: Loaded ${allNodes.length} items in ${pageCount} pages (${duration.toFixed(1)}ms)`
      );

    // Cache the complete result
    this.cache.set(cacheKey, allNodes);

    return allNodes;
  }

  /**
   * Transform raw Storefront API metaobject nodes into the format expected
   * by the data pipeline (matching the Liquid output format).
   *
   * The data pipeline expects metaobjects with flattened fields, e.g.:
   * { id: "gid://...", handle: "walnut", type: "Walnut Veneer", sku: "WN", ... }
   *
   * Storefront API returns nested fields:
   * { id: "gid://...", handle: "walnut", fields: [{key: "type", value: "Walnut Veneer"}, ...] }
   *
   * @param {Array} nodes - Raw metaobject nodes from Storefront API
   * @returns {Array} Transformed metaobjects with flattened fields
   */
  transformNodes(nodes) {
    return nodes.map((node) => {
      const transformed = {
        id: node.id,
        handle: node.handle,
      };

      // Flatten fields into top-level properties
      if (node.fields && Array.isArray(node.fields)) {
        node.fields.forEach((field) => {
          // Parse JSON values (references, lists, etc.)
          let value = field.value;

          if (value && typeof value === 'string') {
            // Try to parse JSON values (lists, references, booleans, numbers)
            try {
              const parsed = JSON.parse(value);
              value = parsed;
            } catch (_e) {
              // Keep as string if not valid JSON
            }
          }

          transformed[field.key] = value;
        });
      }

      return transformed;
    });
  }

  /**
   * Fetch ALL 4 metaobject types in parallel.
   * Returns complete dataset for PPD creation.
   *
   * Types fetched:
   * 1. wood - Wood species options
   * 2. finishes - Finish options
   * 3. forbidden_condition_group - Forbidden combination condition groups
   * 4. forbidden_rule_unified - Unified forbidden combination rules
   *
   * @param {number} batchSize - Items per page (default 250, use smaller for testing)
   * @returns {Promise<{woodMetaobjects: Array, finishMetaobjects: Array, forbiddenConditionGroups: Array, forbiddenRulesUnified: Array, metadata: Object}>}
   */
  async fetchAllMetaobjects(batchSize = 250) {
    isDev() &&
      console.log(
        '[DataFetcher] Starting parallel fetch of all 4 metaobject types...'
      );
    const startTime = performance.now();

    this.progress = {
      total: 4,
      loaded: 0,
      types: {},
    };

    try {
      // 🔄 ENHANCED: Use Promise.allSettled for better failure recovery
      // This allows partial success - if one type fails, others can still succeed
      const results = await Promise.allSettled([
        this.fetchAllOfType('wood', batchSize),
        this.fetchAllOfType('finishes', batchSize),
        this.fetchAllOfType('forbidden_condition_group', batchSize),
        this.fetchAllOfType('forbidden_rule_unified', batchSize),
      ]);

      // Extract results with fallback to empty arrays for failures
      const typeNames = [
        'wood',
        'finishes',
        'forbidden_condition_group',
        'forbidden_rule_unified',
      ];
      const failures = [];

      const [woodNodes, finishNodes, conditionGroupNodes, ruleNodes] =
        results.map((result, index) => {
          if (result.status === 'fulfilled') {
            return result.value;
          } else {
            const typeName = typeNames[index];
            failures.push({ type: typeName, error: result.reason });
            isDev() &&
              console.error(
                `[DataFetcher] ⚠️ Failed to fetch ${typeName}:`,
                result.reason
              );
            return []; // Fallback to empty array
          }
        });

      // Transform from Storefront API format to pipeline-expected format
      const woodMetaobjects = this.transformNodes(woodNodes);
      const finishMetaobjects = this.transformNodes(finishNodes);
      const forbiddenConditionGroups = this.transformNodes(conditionGroupNodes);
      const forbiddenRulesUnified = this.transformNodes(ruleNodes);

      const duration = performance.now() - startTime;
      const totalCount =
        woodMetaobjects.length +
        finishMetaobjects.length +
        forbiddenConditionGroups.length +
        forbiddenRulesUnified.length;

      // Log success or partial success
      if (failures.length === 0) {
        isDev() &&
          console.log(
            `[DataFetcher] ✅ All metaobjects fetched successfully in ${duration.toFixed(1)}ms`,
            {
              woodMetaobjects: woodMetaobjects.length,
              finishMetaobjects: finishMetaobjects.length,
              forbiddenConditionGroups: forbiddenConditionGroups.length,
              forbiddenRulesUnified: forbiddenRulesUnified.length,
              totalItems: totalCount,
            }
          );
      } else {
        isDev() &&
          console.warn(
            `[DataFetcher] ⚠️ Partial success: ${failures.length} type(s) failed to fetch in ${duration.toFixed(1)}ms`,
            {
              woodMetaobjects: woodMetaobjects.length,
              finishMetaobjects: finishMetaobjects.length,
              forbiddenConditionGroups: forbiddenConditionGroups.length,
              forbiddenRulesUnified: forbiddenRulesUnified.length,
              totalItems: totalCount,
              failures: failures.map((f) => f.type),
            }
          );
      }

      return {
        woodMetaobjects,
        finishMetaobjects,
        forbiddenConditionGroups,
        forbiddenRulesUnified,
        metadata: {
          fetchDuration: duration,
          totalItems: totalCount,
          timestamp: Date.now(),
          hasFailures: failures.length > 0,
          failures: failures,
        },
      };
    } catch (error) {
      // This should only trigger if Promise.allSettled itself fails (rare)
      isDev() &&
        console.error(
          '[DataFetcher] ❌ Critical error during metaobject fetch:',
          error
        );

      // Return empty data structures to allow configurator to continue
      // (better to show something than a blank page)
      return {
        woodMetaobjects: [],
        finishMetaobjects: [],
        forbiddenConditionGroups: [],
        forbiddenRulesUnified: [],
        metadata: {
          fetchDuration: performance.now() - startTime,
          totalItems: 0,
          timestamp: Date.now(),
          hasFailures: true,
          criticalError: error.message,
        },
      };
    }
  }

  // REMOVED: injectIntoDOM() method deleted - no longer needed.
  // Metaobject data now flows directly through rawData parameter passing
  // in the pipeline instead of being injected into DOM script elements.
  // See: md-config-core-initializer.js fetchMetaobjects() -> rawData merge -> pipeline

  /**
   * Throttle helper - delays execution for specified milliseconds
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise<void>}
   */
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Get current fetch progress
   * @returns {Object} Progress object with total, loaded, and per-type details
   */
  getProgress() {
    return { ...this.progress };
  }

  /**
   * Clear the internal cache (useful for testing or forced refresh)
   */
  clearCache() {
    this.cache.clear();
    isDev() && console.log('[DataFetcher] Cache cleared');
  }
}

// Export for use by initializer (Phase 1 Bootstrap)
window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
window.MurphyDoorConfigurator.DataFetcher = DataFetcher;

export { DataFetcher };
